import React, { useEffect, useState } from "react";
// AMPLIFY
import { Auth } from "aws-amplify";
// REACT BOOTSTRAP
import Collapse from "react-bootstrap/Collapse";
// ANT DESIGN COMPONENTS
import { Input, Button, Spin } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faKey } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateAmplifyActiveComponent,
  updateUser,
  updateIsLoading,
} from "../../redux/login/loginSlice";

type DomainMap = { [key: string]: string };

export const DOMAIN_TENANT_MAP: DomainMap = {
  "outlook.com": "azureDigisol",
  "flynashville.com": "azureBNA",
  "christianacare.org": "azureChristiana",
};

function CustomAmplifySignIn() {
  // ************************************************ */
  // LOCAL STORAGE AND VARIABLES ******************** */
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [loginErrorMessage, setLoginErrorMessage] = useState<any>("");
  const [openCollapseTotpToke, setOpenCollapseTotpToken] = useState(false);
  const [openCollapsePsw, setOpenCollapsePsw] = useState(false);
  const [totpToken, setTotpToken] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user } = useSelector((state: any) => state.login);

  if (user === null || user === undefined) {
    Auth.currentAuthenticatedUser().then((data: any) => {
      dispatch(updateUser(data));
    });
  }

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleChangeCleanForm = () => {
    dispatch(updateIsLoading(false));
    setOpenCollapseTotpToken(false);
    setOpenCollapsePsw(false);
    setEmailError(false);
    setLoginErrorMessage("");
    setEmail("");
    setPassword("");
    setTotpToken("");
    setIsLoading(false);
  };

  const handleSubmitSingIn = async () => {
    try {
      dispatch(updateIsLoading(true));
      setLoginErrorMessage("");
      const response = await Auth.signIn(email, password);
      if (response) {
        setOpenCollapsePsw(true);
        dispatch(updateUser(response));
        if (response.challengeName && response.challengeName === "MFA_SETUP") {
          setTimeout(() => {
            dispatch(updateAmplifyActiveComponent("mfa-setup"));
            dispatch(updateIsLoading(false));
          }, 1000);
        } else if (response.challengeName && response.challengeName === "SOFTWARE_TOKEN_MFA") {
          setOpenCollapseTotpToken(true);
          dispatch(updateIsLoading(false));
        } else if (response.challengeName && response.challengeName === "NEW_PASSWORD_REQUIRED") {
          setTimeout(() => {
            dispatch(updateAmplifyActiveComponent("force-change-password"));
            dispatch(updateIsLoading(false));
          }, 1000);
        }
      }
    } catch (error) {
      dispatch(updateIsLoading(false));
      setLoginErrorMessage(error);
    }
  };
  const getDomainFromEmail = (): string | undefined => {
    const atIndex = email.indexOf("@");
    if (atIndex === -1) return undefined; // Handle invalid email
    return email.slice(atIndex + 1);
  };

  const getIdPName = (domain: string): string | undefined => DOMAIN_TENANT_MAP[domain];

  const handleSubmitSingInSSO = async () => {
    try {
      dispatch(updateIsLoading(true));
      setLoginErrorMessage("");
      const domain = getDomainFromEmail() || "";
      const customProviderName = getIdPName(domain) || "";
      if (customProviderName === undefined || customProviderName === "") {
        throw new Error("Not a valid email for SSO");
      } else {
        await Auth.federatedSignIn({ customProvider: customProviderName }); // redirect  LOGIN  Azure AD
      }
    } catch (error) {
      dispatch(updateIsLoading(false));
      setLoginErrorMessage(error);
    }
  };
  const handleSubmitConfirmSignIn = async () => {
    try {
      dispatch(updateIsLoading(true));
      setLoginErrorMessage("");
      await Auth.confirmSignIn(
        user, // Return object from Auth.signIn()
        totpToken, // Confirmation code
        "SOFTWARE_TOKEN_MFA", // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
      );
      dispatch(updateIsLoading(false));
    } catch (error) {
      dispatch(updateIsLoading(false));
      setLoginErrorMessage(error);
    }
  };

  const handleSubmitSignInEnter = (event: any) => {
    if (event.key === "Enter") {
      handleSubmitSingIn();
    }
  };

  const handleSubmitConfirmSignInEnter = (event: any) => {
    if (event.key === "Enter") {
      handleSubmitConfirmSignIn();
    }
  };

  const validateEmail = (e: string) =>
    String(e)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    const emailValid = validateEmail(email);
    if (emailValid) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  }, [email]);

  useEffect(() => {
    setEmail("");
    setPassword("");
    //
    localStorage.removeItem("email");
    localStorage.removeItem("password");
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("password");
    //
    setIsLoading(false);
  }, []);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={isLoading}>
      {!isLoading && (
        <>
          <div>
            {/* INPUT - EMAIL */}
            <Input
              placeholder="Email"
              status={emailError && email.length > 0 ? "error" : ""}
              prefix={
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="generalStyles__info generalStyles__mrFix"
                />
              }
              onChange={(e) => {
                setEmail(e.target.value.toLocaleLowerCase());
              }}
              disabled={openCollapseTotpToke}
              value={email}
              autoComplete="off"
            />
            {/* INPUT - PASSWORD */}
            <Input.Password
              className="mt-3"
              placeholder="Password"
              prefix={
                <FontAwesomeIcon
                  className="generalStyles__info generalStyles__mrFix"
                  icon={faLock}
                />
              }
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              onKeyDown={handleSubmitSignInEnter}
              disabled={emailError || openCollapseTotpToke}
              value={password}
              autoComplete="off"
            />
            {/* BUTTON - PASSWORD */}
            <Collapse in={openCollapseTotpToke}>
              <div className="mt-4 mb-4">
                {/* INPUT - TOTP TOKEN */}
                <Input
                  placeholder="Token"
                  prefix={
                    <FontAwesomeIcon
                      className="generalStyles__info generalStyles__mrFix"
                      icon={faKey}
                    />
                  }
                  onChange={(e) => {
                    setTotpToken(e.target.value);
                  }}
                  onKeyDown={handleSubmitConfirmSignInEnter}
                  value={totpToken}
                />
              </div>
            </Collapse>
            {!openCollapseTotpToke && !openCollapsePsw ? (
              <div className="generalStyles__spaceBetween generalStyles__width100 mt-3">
                <div>
                  <Button
                    className={`${
                      emailError || password.length === 0 ? "" : "buttonStyle__1"
                    } generalStyles__width100px `}
                    disabled={password.length === 0 || emailError}
                    onClick={handleSubmitSingIn}
                  >
                    Login
                  </Button>
                  <Button
                    className={`${emailError ? "" : "buttonStyle__1"} generalStyles__mlFix `}
                    onClick={handleSubmitSingInSSO}
                    disabled={emailError}
                  >
                    Active Directory Login
                  </Button>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {
                    dispatch(updateAmplifyActiveComponent("forgot-password"));
                  }}
                  onClick={() => {
                    dispatch(updateAmplifyActiveComponent("forgot-password"));
                  }}
                  className="loginStyles__forgotPasswordButton"
                >
                  <div style={{ marginTop: "-10px" }}>
                    <span>Forgot Password?</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Button
                  className={`${
                    totpToken.length < 5 ? "" : "buttonStyle__1 "
                  } generalStyles__width150px`}
                  disabled={totpToken.length < 5}
                  onClick={handleSubmitConfirmSignIn}
                >
                  Login
                </Button>
                <Button
                  disabled={password.length === 0 || emailError}
                  className=" generalStyles__mlFix"
                  type="default"
                  onClick={handleChangeCleanForm}
                >
                  <span>Sign in With Another Account</span>
                </Button>
              </>
            )}
          </div>
          <p className="loginStyle__errorMessage">{loginErrorMessage.message}</p>
        </>
      )}
    </Spin>
  );
}

export default CustomAmplifySignIn;
